import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, InputBase, Typography } from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import FormStatusAlert from "../../components/FormStatusAlert";
import SignatureControls from "../../components/SignatureControls";

const PDFEditorHeader = ({
  title,
  onTitleChange,
  onSave,
  onClose,
  onFinalizeSuccess,
  loading,
  setOpenPreviewDialog,
  formData,
  isNew,
  openSettingsDialog,
  setOpenSettingsDialog,
  onUpdate,
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title || "Untitled Form");
  const [openStatusAlert, setOpenStatusAlert] = useState(false);

  useEffect(() => {
    setDraftTitle(title || "Untitled Form");
  }, [title]);

  const commitTitle = () => {
    const finalTitle = draftTitle?.trim() || "Untitled Form";
    onTitleChange?.(finalTitle);
    setDraftTitle(finalTitle);
    setIsEditingTitle(false);
  };

  const handleCloseStatusAlert = () => {
    setOpenStatusAlert(false);
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "white",
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              minWidth: 0,
            }}
          >
            {isEditingTitle ? (
              <InputBase
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitTitle();
                  if (e.key === "Escape") {
                    setDraftTitle(title || "Untitled Form");
                    setIsEditingTitle(false);
                  }
                }}
                autoFocus
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  borderBottom: "1px solid #1976d2",
                  minWidth: 220,
                }}
              />
            ) : (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title || "Untitled Form"}
              </Typography>
            )}

            <IconButton
              size="small"
              onClick={() =>
                isEditingTitle ? commitTitle() : setIsEditingTitle(true)
              }
            >
              {isEditingTitle ? (
                <CheckIcon fontSize="small" />
              ) : (
                <EditIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
          <SignatureControls
            signatures={
              formData?.updated_settings?.signatures ||
              formData?.guessedFormDetails?.signatures
            }
            onUpdate={onUpdate}
            disabled={loading}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => setOpenSettingsDialog(true)}
            disabled={loading}
          >
            Document Settings
          </Button>
          <Button
            variant="contained"
            onClick={() => setOpenPreviewDialog(true)}
            disabled={loading}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            onClick={() => onSave(false)}
            disabled={loading}
            sx={{ width: "max-content" }}
          >
            Save Changes
          </Button>
          {(formData?.status === "inactive" || isNew) && (
            <Button
              variant="contained"
              onClick={() => setOpenStatusAlert(true)}
              disabled={loading}
              sx={{ bgcolor: "green", width: "max-content" }}
            >
              Finalize Form
            </Button>
          )}
          <IconButton onClick={onClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <FormStatusAlert
        status={"active"}
        open={openStatusAlert}
        handleClose={handleCloseStatusAlert}
        formId={formData?.form_id || formData?.formId}
        beforeStatusChange={() => onSave(false, { silent: true })}
        fetchData={onFinalizeSuccess || onClose}
      />
    </>
  );
};

export default PDFEditorHeader;
